import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramLatentHeat } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.heatTransferLatent.v1';

type Fields = { airFlow: string; humidityDiff: string; latentHeat: string };
const EMPTY: Fields = { airFlow: '', humidityDiff: '', latentHeat: '' };

/**
 * Latent Heat Transfer — Q_l = 0.68 × CFM × ΔW
 * ΔW in grains (grains of moisture per lb dry air) — kept in Imperial (grains).
 */
const HeatTransferLatentHeatTransferScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const cfm = toImp(fields.airFlow, 'flow');
    const dw = parseFloat(fields.humidityDiff); // grains/lb — universal in this screen
    const ql = toImp(fields.latentHeat, 'heatRate');

    if (fields.latentHeat === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      setSolvedFor('latentHeat');
      set({ latentHeat: fromImp(0.68 * cfm * dw, 'heatRate', 0) });
    } else if (fields.airFlow === '') {
      if (dw === 0) { setError('Humidity difference cannot be zero.'); return; }
      setSolvedFor('airFlow');
      set({ airFlow: fromImp(ql / (0.68 * dw), 'flow', 1) });
    } else if (fields.humidityDiff === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      setSolvedFor('humidityDiff');
      set({ humidityDiff: (ql / (0.68 * cfm)).toFixed(4) });
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.airFlow && { label: 'Air Flow', value: fields.airFlow, unit: unit('flow'), emphasis: solvedFor === 'airFlow' },
    fields.humidityDiff && { label: 'Humidity Difference', value: fields.humidityDiff, unit: 'gr/lb', emphasis: solvedFor === 'humidityDiff' },
    fields.latentHeat && { label: 'Latent Heat Transfer', value: fields.latentHeat, unit: unit('heatRate'), emphasis: solvedFor === 'latentHeat' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Latent Heat Transfer"
      subtitle="Calculate latent heat transfer rate from air flow and humidity difference"
      formula={
        <>
          <FormulaLine>{isImperial ? 'Q_l = 0.68 × CFM × ΔW' : 'Q_l = 0.42 × L/s × ΔW'}</FormulaLine>
          <FormulaNote>Q_l = Latent Heat Transfer ({unit('heatRate')}), {isImperial ? 'CFM' : 'L/s'} = Air Flow Rate ({unit('flow')}), ΔW = Humidity Ratio Difference (grains/lb)</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formula: Q_l = 0.68 × CFM × ΔW' : 'Formula: Q_l = 0.42 × L/s × ΔW'}
      diagram={<DiagramLatentHeat />}
      outputSlots={{
        cfm: fields.airFlow ? toImp(fields.airFlow, 'flow') : undefined,
        humidity_ratio_lb_lb: fields.humidityDiff ? parseFloat(fields.humidityDiff) / 7000 : undefined,
        latent_heat_btuh: fields.latentHeat ? toImp(fields.latentHeat, 'heatRate') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.airFlow) patch.airFlow = fromImp(values.cfm, 'flow', 1);
        if (values.humidity_ratio_lb_lb != null && !fields.humidityDiff) patch.humidityDiff = (values.humidity_ratio_lb_lb * 7000).toFixed(4);
        if (values.latent_heat_btuh != null && !fields.latentHeat) patch.latentHeat = fromImp(values.latent_heat_btuh, 'heatRate', 0);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Air Flow"
          value={fields.airFlow}
          onChangeText={(v) => set({ airFlow: v })}
          rightText={unit('flow')}
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Humidity Difference"
          value={fields.humidityDiff}
          onChangeText={(v) => set({ humidityDiff: v })}
          rightText="gr/lb"
          maxDecimals={4}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Latent Heat Transfer"
          value={fields.latentHeat}
          onChangeText={(v) => set({ latentHeat: v })}
          rightText={unit('heatRate')}
          maxDecimals={0}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default HeatTransferLatentHeatTransferScreen;
